import { ApiErrorData, ApiResponse } from 'models/ApiResponse';
import { DiIngestJob } from 'models/DiIngestJob';
import { DiIngestJobGridMode } from 'models/DiIngestJobGridMode';
import { DiParsingFormat } from 'models/DiParsingFormat';
import { ServerSideDataSchema } from 'models/ServerSideDataSchema';

import { DiTableColumn } from 'services/DiTableColumn';

import { FunctionsHelper } from 'utils/FunctionsHelper';

import MainBackendAxios from '../utils/MainBackendAxios';

export const DiIngestJobService = {
  getByCurrentCompany: async (
    body: GetByCurrentCompanyRequestDto
  ): Promise<
    ApiResponse<ServerSideDataSchema<DiIngestJobGridMode> | ApiErrorData>
  > => {
    try {
      const response = await MainBackendAxios.post(
        `/di/ingest-job/get-by-current-company`,
        body
      );

      return {
        success: true,
        status: response.status,
        data: response?.data?.data,
      };
    } catch (err: any) {
      return FunctionsHelper.getErrorFormat(err);
    }
  },
  findById: async (
    id: number
  ): Promise<ApiResponse<DiIngestJob | ApiErrorData>> => {
    try {
      const response = await MainBackendAxios.get(
        `/di/ingest-job/find-by-id/${id}`
      );

      return {
        success: true,
        status: response.status,
        data: response?.data?.data,
      };
    } catch (err: any) {
      return FunctionsHelper.getErrorFormat(err);
    }
  },
  findGridModeById: async (
    id: number
  ): Promise<ApiResponse<DiIngestJobGridMode | ApiErrorData>> => {
    try {
      const response = await MainBackendAxios.get(
        `/di/ingest-job/find-grid-mod-by-id/${id}`
      );

      return {
        success: true,
        status: response.status,
        data: response?.data?.data,
      };
    } catch (err: any) {
      return FunctionsHelper.getErrorFormat(err);
    }
  },
  create: async (
    clientId: number,
    description: string,
    file: File
  ): Promise<ApiResponse<{ createdId: number } | ApiErrorData>> => {
    try {
      const formData = new FormData();
      formData.append('clientId', `${clientId}`);
      formData.append('description', description);
      formData.append('file', file);

      const response = await MainBackendAxios.post(
        `/di/ingest-job/create`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      return {
        success: true,
        status: response.status,
        data: response?.data?.data,
      };
    } catch (err: any) {
      return FunctionsHelper.getErrorFormat(err);
    }
  },
  matchColumns: async (
    id: number,
    body: MatchColumnsRequestDto
  ): Promise<ApiResponse<{ executedAction: boolean } | ApiErrorData>> => {
    try {
      const response = await MainBackendAxios.put(
        `/di/ingest-job/match-columns/${id}`,
        body
      );

      return {
        success: true,
        status: response.status,
        data: response?.data?.data,
      };
    } catch (err: any) {
      return FunctionsHelper.getErrorFormat(err);
    }
  },
  cancel: async (
    id: number
  ): Promise<ApiResponse<{ executedAction: boolean } | ApiErrorData>> => {
    try {
      const response = await MainBackendAxios.get(
        `/di/ingest-job/cancel/${id}`
      );

      return {
        success: true,
        status: response.status,
        data: response?.data?.data,
      };
    } catch (err: any) {
      return FunctionsHelper.getErrorFormat(err);
    }
  },
  getAllTableColumnsByCurrentCompany: async (): Promise<
    ApiResponse<DiTableColumn[] | ApiErrorData>
  > => {
    try {
      const response = await MainBackendAxios.get(
        `/di/table-column-config/get-table-columns-by-current-company`
      );

      return {
        success: true,
        status: response.status,
        data: response?.data?.data,
      };
    } catch (err: any) {
      return FunctionsHelper.getErrorFormat(err);
    }
  },
  getAllParsingFormats: async (): Promise<
    ApiResponse<DiParsingFormat[] | ApiErrorData>
  > => {
    try {
      const response = await MainBackendAxios.get(`/di/parsing-format/get-all`);

      return {
        success: true,
        status: response.status,
        data: response?.data?.data,
      };
    } catch (err: any) {
      return FunctionsHelper.getErrorFormat(err);
    }
  },
};

export interface GetByCurrentCompanyRequestDto {
  page: number;
  pageSize: number;
  term?: string;
  statuses?: string[];
  clientId?: number;
}

export interface MatchColumnsRowSchema {
  extractedColumnId: number;
  tableColumnConfigId: number | null;
  parsingFormatId: number | null;
}

export interface MatchColumnsRequestDto {
  rows: MatchColumnsRowSchema[];
}
